/**
 * usePresence — soft presence beacon for /social and /notifications drawers.
 *
 * Called from inside a draft drawer / detail view:
 *
 *   usePresence({ resourceType: 'notification_draft', resourceId: draftId });
 *
 * Lifecycle:
 *   1. On mount: POST /admin/presence/{type}/{id}/open.
 *   2. While mounted: heartbeat every 15s via POST .../heartbeat.
 *   3. On unmount: POST .../close. Also fires on `beforeunload` so a tab
 *      close releases presence eagerly without waiting on the 60s sweep
 *      TTL.
 *
 * Failure handling: every call is fire-and-forget (`.catch(() => {})`).
 * Presence is purely UX coordination — failing silently is the right call.
 * The 60s server-side sweep guarantees rows TTL out even if /close never
 * fires (network drop, browser kill, etc.).
 *
 * Plan reference: docs/wip/assayer-stream/MULTI_OPERATOR_CONCURRENCY_PLAN.md (Layer 3)
 */

import { useEffect } from 'react';
import {
  closePresence,
  heartbeatPresence,
  openPresence,
  type PresenceResourceType,
} from '../lib/api/presence.js';

interface UsePresenceOptions {
  resourceType: PresenceResourceType;
  /** Pass null/undefined when the drawer is closed — hook becomes a no-op. */
  resourceId: string | number | null | undefined;
  /** Override the 15s heartbeat for testing. Don't change in prod — the
   *  60s server TTL assumes 4× heartbeat-to-TTL ratio. */
  heartbeatIntervalMs?: number;
}

const DEFAULT_HEARTBEAT_MS = 15_000;

export function usePresence({
  resourceType,
  resourceId,
  heartbeatIntervalMs = DEFAULT_HEARTBEAT_MS,
}: UsePresenceOptions): void {
  useEffect(() => {
    if (resourceId === null || resourceId === undefined) return;
    let cancelled = false;

    // Best-effort open. If this fails (e.g. backend hiccup), the chip
    // simply won't appear for other operators — no UX regression beyond
    // what already exists today.
    openPresence(resourceType, resourceId).catch(() => {});

    const interval = window.setInterval(() => {
      if (cancelled) return;
      heartbeatPresence(resourceType, resourceId).catch(() => {});
    }, heartbeatIntervalMs);

    // Eager release on tab close. fetch with `keepalive` would be ideal
    // but axios doesn't expose that flag; navigator.sendBeacon would mean
    // duplicating the URL builder. The non-keepalive POST is usually
    // accepted by Cloud Run because the request is small + fires before
    // the page unloads; if it doesn't, the 60s TTL sweep cleans up anyway.
    const onBeforeUnload = () => {
      closePresence(resourceType, resourceId).catch(() => {});
    };
    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('beforeunload', onBeforeUnload);
      closePresence(resourceType, resourceId).catch(() => {});
    };
  }, [resourceType, resourceId, heartbeatIntervalMs]);
}
